/**
 * Global window.Veyop facade for third-party scripts and addons (semver-stable surface).
 *
 * Addon authors: see plugins/text-arc/ for the canonical usage pattern.
 *   - Listen to lifecycle events via window.Veyop.hooks.on('veyop:designer:ready', ...)
 *   - Register UI extensions via window.Veyop.tools and window.Veyop.itemRenderers
 *
 * Re-registering with the same id overwrites the previous entry. Registries are
 * reactive — host UI updates automatically when an addon registers/unregisters.
 */
import { eventBus } from './EventBus';
import { bootReport } from './bootTiming';
import type { DesignEventMap, DesignItem } from './types';
import type { AddItemOptions } from './itemPlacement';
import { centerItemInPrintArea } from './itemPlacement';
import { fitDesignItemsToPrintAreas, printSafetyMargins, readAuthoringAreaSizes } from './templateAreaScale';
import { measureItemContent } from './itemContentBounds';
import { resolveViewAreaSizes, resolveViewPhysicalSizesCm } from '../designer-app/utils/printArea';
import type { Pinia } from 'pinia';
import { notifyError, notifyInfo, notifySuccess, notifyWarning } from '../shared/notify';
import {
  addAction,
  addFilter,
  applyFilters,
  doAction,
  getSlotEntries,
  registerSlot,
  removeAction,
  removeFilter,
  removeSlot,
  type VeyoHookSlotEntry,
} from './VeyoHooks';
import {
  registerTextTool,
  getTextTools,
  registerImageTool,
  getImageTools,
  registerItemRenderer,
  findItemRenderer,
  registerEditPanel,
  getEditPanels,
  findEditPanel,
  registerItemBoundsResolver,
  getItemBoundsResolvers,
  resolveItemBounds,
  fitItemToBox,
  registerTextStyleSupport,
  resolveTextStyleSupport,
  registerAdminPage,
  getAdminPages,
  findAdminPage,
  registerAdminPageTab,
  getAdminPageTabs,
  registerAdminWidget,
  getAdminWidgets,
  registerWooProductSection,
  getWooProductSections,
  registerPrintingMethodProvider,
  getPrintingMethodProviders,
  resolvePrintingMethodProviderId,
  registerCatalogEditPanel,
  registerCatalogListBadges,
  getCatalogEditPanels,
  registerCatalogCanvasOverlay,
  getCatalogCanvasOverlays,
  registerDesignerOverlay,
  getDesignerOverlays,
  registerStatusPill,
  getStatusPills,
  registerDesignerLeftPanel,
  getDesignerLeftPanels,
  findDesignerLeftPanel,
  registerDesignerLayout,
  getDesignerLayouts,
  findDesignerLayout,
  registerUploadProcessor,
  getUploadProcessors,
  uploadImage,
  registerImageRasterizer,
  getImageRasterizers,
  registerImageExporter,
  getImageExporters,
  registerPrintMethodPack,
  getPrintMethodPacks,
  registerMobileNavItem,
  getMobileNavItems,
  registerToolbarAction,
  getToolbarActions,
  type VeyopTextTool,
  type VeyopImageTool,
  type VeyopItemRenderer,
  type VeyopEditPanel,
  type VeyopItemBounds,
  type VeyopItemBoundsResolver,
  type VeyopTextStyleSupport,
  type VeyopTextStyleSupportProvider,
  type VeyopAdminPage,
  type VeyopAdminPageTab,
  type VeyopAdminWidget,
  type VeyopAdminWidgetPlacement,
  type VeyopWooProductSection,
  type VeyopPrintingMethodProvider,
  type VeyopCatalogEditPanel,
  type VeyopCatalogListBadgeProvider,
  type VeyopCatalogCanvasOverlay,
  type VeyopDesignerOverlay,
  type VeyopStatusPill,
  type VeyopDesignerLeftPanel,
  type VeyopDesignerLayout,
  type VeyopUploadProcessor,
  type VeyopUploadedImage,
  registerItemSvgExporter,
  getItemSvgExporters,
  registerPreflightRule,
  getPreflightRules,
  registerPrintAreaShape,
  getPrintAreaShapes,
  registerProductView,
  getProductViews,
  resolveProductView,
  isViewKey,
  CORE_VIEW_NAMES,
} from './extensionRegistry';
import {
  registerEffectPreset,
  getEffectPresets,
  findEffectPreset,
} from './imagePipeline/effectPresets';

export interface VeyopRuntimeHooks {
  on(event: string, handler: (payload: unknown) => void): void;
  off(event: string, handler: (payload: unknown) => void): void;
  emit(event: string, payload?: unknown): void;
  addFilter(name: string, namespace: string, callback: (...args: unknown[]) => unknown, priority?: number): () => void;
  removeFilter(name: string, namespace?: string): void;
  applyFilters<T>(name: string, value: T, ...args: unknown[]): T;
  addAction(name: string, namespace: string, callback: (...args: unknown[]) => unknown, priority?: number): () => void;
  removeAction(name: string, namespace?: string): void;
  doAction(name: string, ...args: unknown[]): void;
  registerSlot(name: string, namespace: string, render: VeyoHookSlotEntry['render'], priority?: number): () => void;
  removeSlot(name: string, namespace?: string): void;
  getSlotEntries(name: string): VeyoHookSlotEntry[];
  whenReady(surface: VeyopRuntimeSurface, handler: (payload: unknown) => void): () => void;
}

export type VeyopRuntimeSurface = 'designer' | 'admin';

export interface VeyopDesignerReadyPayload {
  config: unknown;
  hydrated: true;
  source: 'new' | 'cart' | 'saved' | 'template';
  productId: string | number;
  cartId?: string;
  warnings: string[];
}

export interface VeyopRuntimeTools {
  registerTextTool(tool: VeyopTextTool): () => void;
  getTextTools(): VeyopTextTool[];
  registerImageTool(tool: VeyopImageTool): () => void;
  getImageTools(): VeyopImageTool[];
}

export interface VeyopRuntimeItemRenderers {
  register(renderer: VeyopItemRenderer): () => void;
  find(item: DesignItem): VeyopItemRenderer | null;
}

export interface VeyopRuntimePanels {
  register(panel: VeyopEditPanel): () => void;
  getEditPanels(): VeyopEditPanel[];
  find(id: string): VeyopEditPanel | null;
}

export interface VeyopRuntimeItemBounds {
  register(resolver: VeyopItemBoundsResolver): () => void;
  getResolvers(): VeyopItemBoundsResolver[];
  resolve(item: DesignItem): VeyopItemBounds | null;
  /** Patch for a box the user dragged to, from the resolver that owns the item. */
  fitToBox(item: DesignItem, box: VeyopItemBounds): Record<string, unknown> | null;
  /** What the layer really paints (addon resolver, else measured text); null when that is its box. */
  measureContent(item: DesignItem): VeyopItemBounds | null;
}

export interface VeyopRuntimeTextStyles {
  register(provider: VeyopTextStyleSupportProvider): () => void;
  resolve(item: DesignItem | null | undefined): VeyopTextStyleSupport | null;
}

export interface VeyopRuntimeAdmin {
  registerPage(page: VeyopAdminPage): () => void;
  getPages(): VeyopAdminPage[];
  findPage(path: string): VeyopAdminPage | null;
  registerPageTab(tab: VeyopAdminPageTab): () => void;
  getPageTabs(pageId: string): VeyopAdminPageTab[];
  registerWidget(widget: VeyopAdminWidget): () => void;
  getWidgets(placement?: VeyopAdminWidgetPlacement): VeyopAdminWidget[];
  registerWooProductSection(section: VeyopWooProductSection): () => void;
  getWooProductSections(): VeyopWooProductSection[];
  registerPrintingMethodProvider(provider: VeyopPrintingMethodProvider): () => void;
  getPrintingMethodProviders(): VeyopPrintingMethodProvider[];
  resolvePrintingMethodProviderId: typeof resolvePrintingMethodProviderId;
  registerCatalogEditPanel(panel: VeyopCatalogEditPanel): () => void;
  registerCatalogListBadges(provider: VeyopCatalogListBadgeProvider): () => void;
  getCatalogEditPanels(): VeyopCatalogEditPanel[];
  registerCatalogCanvasOverlay(overlay: VeyopCatalogCanvasOverlay): () => void;
  getCatalogCanvasOverlays(): VeyopCatalogCanvasOverlay[];
}

export interface VeyopRuntimeImagePipeline {
  registerEffectPreset: typeof registerEffectPreset;
  getEffectPresets: typeof getEffectPresets;
  findEffectPreset: typeof findEffectPreset;
  registerImageRasterizer: typeof registerImageRasterizer;
  getImageRasterizers: typeof getImageRasterizers;
  registerImageExporter: typeof registerImageExporter;
  getImageExporters: typeof getImageExporters;
  registerPrintMethodPack: typeof registerPrintMethodPack;
  getPrintMethodPacks: typeof getPrintMethodPacks;
  registerItemSvgExporter: typeof registerItemSvgExporter;
  getItemSvgExporters: typeof getItemSvgExporters;
  registerPrintAreaShape: typeof registerPrintAreaShape;
  getPrintAreaShapes: typeof getPrintAreaShapes;
  /**
   * `@font-face` CSS with the font file inlined, for SVG drawn as an image (which cannot see the
   * page's fonts). '' for system fonts or on failure. Bound once the designer has its stores.
   */
  getEmbeddableFontFaceCss?: (family: string, weight?: string, style?: string) => Promise<string>;
}

export interface VeyopRuntimeViews {
  /** Register an addon product view type (e.g. 'sleeve'). Also register it server-side via the `veyop_product_views` PHP filter. */
  register: typeof registerProductView;
  getAll: typeof getProductViews;
  resolve: typeof resolveProductView;
  isViewKey: typeof isViewKey;
  coreViews: typeof CORE_VIEW_NAMES;
}

export interface VeyopRuntimeUploads {
  registerProcessor(processor: VeyopUploadProcessor): () => void;
  getProcessors(): VeyopUploadProcessor[];
  /** Store a file through the shopper upload; rejects until the designer has mounted. */
  uploadImage(file: File): Promise<VeyopUploadedImage>;
}

/** Minimal structural shape core needs from a preflight report; the real
 * report (PreflightReportV2) is owned by the preflight addon. */
export interface VeyopPreflightReportLike {
  status: string;
  enforcement: string;
  issues: unknown[];
  [key: string]: unknown;
}

export interface VeyopRuntimePreflight {
  registerRule: typeof registerPreflightRule;
  getRules: typeof getPreflightRules;
  /** Provided by the preflight addon on boot; absent when that addon is disabled. */
  buildReport?: (
    items: Record<string, DesignItem[]>,
    options: Record<string, unknown>,
  ) => VeyopPreflightReportLike | null;
  normalizeProfile?: (raw: unknown) => Record<string, unknown>;
}

export interface VeyopRuntimeDesigner {
  registerOverlay(overlay: VeyopDesignerOverlay): () => void;
  getOverlays(): VeyopDesignerOverlay[];
  registerStatusPill(pill: VeyopStatusPill): () => void;
  getStatusPills(): VeyopStatusPill[];
  registerLeftPanel(panel: VeyopDesignerLeftPanel): () => void;
  getLeftPanels(): VeyopDesignerLeftPanel[];
  findLeftPanel(id: string): VeyopDesignerLeftPanel | null;
  registerLayout(layout: VeyopDesignerLayout): () => void;
  getLayouts(): VeyopDesignerLayout[];
  findLayout(themeSlug?: string): VeyopDesignerLayout | null;
  components?: Record<string, unknown>;

  /** Host Pinia instance (for addons that need to access host stores). */
  pinia?: Pinia;

  /** Stable host commands for item mutation and undo batching. */
  updateItem?: (id: string, patch: Partial<DesignItem>) => void;
  beginHistoryBatch?: () => void;
  endHistoryBatch?: (reason?: string) => void;
  /**
   * Attach addon data that changes the price (sent as `pricing_context` with
   * every quote and add-to-cart, keyed by addon). Pass null to remove it.
   */
  setPricingContext?: (key: string, value: unknown) => void;
  commands?: {
    addItem(item: DesignItem, options?: AddItemOptions): void;
    removeItem(id: string): void;
    updateItem(id: string, patch: Partial<DesignItem>): void;
    selectItem(id: string | null): void;
    loadDesign(state: Record<string, unknown>): void;
    /** Current items per view (read-only snapshot; mutate through updateItem). */
    getItems(): Record<string, DesignItem[]>;
    /** Switch the product color variant by index. */
    setProductColor(index: number): void;
  };
  getContext?: () => {
    productId: string;
    parentId: number;
    baseUrl: string;
    printingCodes: string[];
    activePrintCode: string;
    activePrintMethodPack: string;
    activeView: string;
    embroideryProfile?: unknown;
  };
  resolveAssetUrl?: (raw: string, options?: { proxy?: boolean }) => string;
}

export interface VeyopRuntimeUi {
  registerMobileNavItem: typeof registerMobileNavItem;
  getMobileNavItems: typeof getMobileNavItems;
  registerToolbarAction: typeof registerToolbarAction;
  getToolbarActions: typeof getToolbarActions;
  notify(type: 'success' | 'error' | 'info' | 'warning', message: string): void;
  confirm(message: string): Promise<boolean>;
}

export interface VeyopRuntimeApi {
  request<T = unknown>(path: string, options?: RequestInit): Promise<T>;
}

export function retryAfterMilliseconds(value: string | null, now = Date.now()): number {
  const raw = String(value ?? '').trim();
  if (!raw) return 0;
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds * 1000));
  const date = Date.parse(raw);
  return Number.isFinite(date) ? Math.max(0, date - now) : 0;
}

export class VeyopRequestError extends Error {
  constructor(
    message: string,
    public readonly code: 'timeout' | 'network_error' | 'http_error' | 'invalid_json' | 'invalid_response' | 'aborted',
    public readonly operation: string,
    public readonly status: number,
    public readonly retryable: boolean,
    public readonly requestId: string,
    public readonly retryAfterMs: number = 0,
  ) {
    super(message);
    this.name = 'VeyopRequestError';
  }
}

function runtimeRequestId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `veyop-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * A rejected fetch says nothing about why. The usual field cause is an API URL on another
 * origin than the page (shop domain/SSL configured differently from the admin URL), which
 * the browser blocks as CORS or mixed content: name it so the store owner can fix it.
 */
function networkErrorMessage(url: string): string {
  try {
    const target = new URL(url, window.location.href);
    if (target.origin !== window.location.origin) {
      return `Connection failed: the API (${target.origin}) is on a different domain or protocol than this page (${window.location.origin}). Check the store's domain and SSL settings.`;
    }
  } catch {
    // Unparseable URL: fall through to the generic message.
  }
  return 'Connection failed.';
}

async function runtimeJsonRequest<T>(url: string, path: string, options: RequestInit): Promise<T> {
  const method = String(options.method || 'GET').toUpperCase();
  const readOnly = method === 'GET' || method === 'HEAD';
  const requestId = runtimeRequestId();
  const maxAttempts = readOnly ? 2 : 1;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const controller = new AbortController();
    let timedOut = false;
    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 15_000);
    const externalSignal = options.signal;
    const abortFromExternal = () => controller.abort();
    externalSignal?.addEventListener('abort', abortFromExternal, { once: true });
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: { ...(options.headers ?? {}), 'X-Veyop-Request-ID': requestId },
      });
      const raw = await response.text();
      let payload: Record<string, unknown>;
      try {
        payload = raw.trim() ? JSON.parse(raw) as Record<string, unknown> : {};
      } catch {
        throw new VeyopRequestError('Server returned invalid JSON.', 'invalid_json', path, response.status, false, requestId);
      }
      if (!response.ok || payload.error) {
        const retryable = [502, 503, 504].includes(response.status);
        const retryAfterMs = retryAfterMilliseconds(response.headers.get('Retry-After'));
        throw new VeyopRequestError(
          String(payload.msg || payload.message || `Request failed (${response.status})`),
          'http_error', path, response.status, retryable, requestId, retryAfterMs,
        );
      }
      if (!raw.trim()) throw new VeyopRequestError('Server returned an empty response.', 'invalid_response', path, response.status, false, requestId);
      return (payload.data ?? payload) as T;
    } catch (reason) {
      let error: VeyopRequestError;
      if (reason instanceof VeyopRequestError) error = reason;
      else if (externalSignal?.aborted) error = new VeyopRequestError('Request was cancelled.', 'aborted', path, 0, false, requestId);
      else if (timedOut) error = new VeyopRequestError('Request timed out.', 'timeout', path, 0, true, requestId);
      else error = new VeyopRequestError(networkErrorMessage(url), 'network_error', path, 0, true, requestId);
      if (attempt + 1 >= maxAttempts || !error.retryable) throw error;
      await new Promise((resolve) => window.setTimeout(resolve, 250 + Math.floor(Math.random() * 151)));
    } finally {
      clearTimeout(timer);
      externalSignal?.removeEventListener('abort', abortFromExternal);
    }
  }
  throw new VeyopRequestError('Request failed.', 'network_error', path, 0, false, requestId);
}

export interface VeyopHostRuntimeConfig {
  platform: 'wordpress' | 'opencart' | 'prestashop' | 'standalone';
  baseUrl: string;
  apiUrl: string;
  assetsUrl: string;
  requestHeaders: Record<string, string>;
  /** Swap the WordPress CSRF nonce sent with state-changing requests (no-op on other hosts). */
  setCsrfToken?: (token: string) => void;
}

const WP_CSRF_HEADER = 'X-CSRF-Token';
/** Hidden this long, a tab's nonce is likely stale (WP nonces live 12–24h): refresh on return. */
const CSRF_STALE_AFTER_MS = 6 * 60 * 60 * 1000;

/** A state-changing request the server refused because its CSRF nonce no longer verifies. */
export function isCsrfRejection(error: unknown, method: string): boolean {
  const verb = method.toUpperCase();
  return verb !== 'GET' && verb !== 'HEAD'
    && error instanceof VeyopRequestError
    && error.status === 403
    && /csrf/i.test(error.message);
}

/**
 * The host-neutral HTTP client behind `window.Veyop.http`.
 *
 * WordPress nonces expire after 12–24h and die on re-login, but the admin SPA can stay open for
 * days: every POST then failed with "Invalid CSRF token" while GETs kept working. A request
 * refused for its nonce now fetches a fresh one (`GET /api/v1/csrf`, shared by concurrent
 * failures) and is sent once more.
 */
export function createRuntimeHttp(runtime: VeyopHostRuntimeConfig): {
  http: VeyopRuntimeApi;
  setCsrfToken: (token: string) => void;
  refreshCsrfToken: () => Promise<string>;
} {
  let refreshing: Promise<string> | null = null;
  const usesWpNonce = () => Object.prototype.hasOwnProperty.call(runtime.requestHeaders, WP_CSRF_HEADER);
  const setCsrfToken = (token: string) => {
    if (!token || !usesWpNonce()) return;
    runtime.requestHeaders = { ...runtime.requestHeaders, [WP_CSRF_HEADER]: token };
  };
  runtime.setCsrfToken = setCsrfToken;

  const refreshCsrfToken = (): Promise<string> => {
    refreshing ??= (async () => {
      const response = await fetch(buildRuntimeRequestUrl(runtime, '/api/v1/csrf'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const payload = response.ok ? await response.json().catch(() => null) as { data?: { token?: unknown } } | null : null;
      const token = String(payload?.data?.token ?? '');
      if (!token) throw new Error('CSRF refresh failed');
      setCsrfToken(token);
      return token;
    })().finally(() => { refreshing = null; });
    return refreshing;
  };

  const http: VeyopRuntimeApi = {
    async request<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
      const url = buildRuntimeRequestUrl(runtime, path);
      const send = (override: Record<string, string> = {}) => runtimeJsonRequest<T>(url, path, {
        credentials: 'same-origin',
        ...options,
        headers: {
          ...runtime.requestHeaders,
          ...((options.headers ?? {}) as Record<string, string>),
          ...override,
        },
      });
      try {
        return await send();
      } catch (error) {
        if (!usesWpNonce() || !isCsrfRejection(error, String(options.method || 'GET'))) throw error;
        let token: string;
        try {
          token = await refreshCsrfToken();
        } catch {
          const rejected = error as VeyopRequestError;
          throw new VeyopRequestError('Your session expired. Reload the page to continue.', 'http_error', path, 403, false, rejected.requestId);
        }
        // Callers may pin their own copy of the old nonce in `options.headers`; the fresh one wins.
        return send({ [WP_CSRF_HEADER]: token });
      }
    },
  };
  return { http, setCsrfToken, refreshCsrfToken };
}

/** Refresh ahead of the first failing request when a long-hidden admin tab comes back. */
function trackCsrfStaleness(runtime: VeyopHostRuntimeConfig, http: VeyopRuntimeApi, setCsrfToken: (token: string) => void): void {
  if (typeof document === 'undefined' || !Object.prototype.hasOwnProperty.call(runtime.requestHeaders, WP_CSRF_HEADER)) return;
  let hiddenAt = 0;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    if (!hiddenAt || Date.now() - hiddenAt < CSRF_STALE_AFTER_MS) return;
    hiddenAt = 0;
    void http.request<{ token?: string }>('/api/v1/csrf')
      .then((data) => { if (data?.token) setCsrfToken(String(data.token)); })
      .catch(() => { /* the next state-changing request retries on its own */ });
  });
}

export interface VeyopRuntime {
  /** Aligned with plugin VEYOP_VERSION when built from the same release. */
  version: string;
  /** Versioned façade contract; independent from the plugin release version. */
  apiVersion: 2;
  hooks: VeyopRuntimeHooks;
  /** Preferred portable HTTP client. `api` remains as a compatibility alias. */
  http: VeyopRuntimeApi;
  api: VeyopRuntimeApi;
  runtime: VeyopHostRuntimeConfig;
  tools: VeyopRuntimeTools;
  itemRenderers: VeyopRuntimeItemRenderers;
  panels: VeyopRuntimePanels;
  itemBounds: VeyopRuntimeItemBounds;
  /** Designer start-up timeline (ms since navigation start). */
  perf: { report: () => Array<{ step: string; ms: number }> };
  textStyles: VeyopRuntimeTextStyles;
  admin: VeyopRuntimeAdmin;
  designer: VeyopRuntimeDesigner;
  views: VeyopRuntimeViews;
  uploads: VeyopRuntimeUploads;
  imagePipeline: VeyopRuntimeImagePipeline;
  preflight: VeyopRuntimePreflight;
  ui: VeyopRuntimeUi;
}

/**
 * Build one portable runtime URL without confusing request query parameters
 * with WordPress' query-form `rest_route` value.
 */
export function buildRuntimeRequestUrl(
  runtime: Pick<VeyopHostRuntimeConfig, 'baseUrl' | 'apiUrl'> & Partial<Pick<VeyopHostRuntimeConfig, 'platform'>>,
  path: string,
): string {
  if (/^https?:\/\//i.test(path)) return path;
  // Only WordPress serves the plugin's own `api/index.php`; native hosts route
  // `/api/v1/*` through their adapter endpoint like any other portable path.
  const servesPluginApi = !runtime.platform || runtime.platform === 'wordpress';
  if (path.startsWith('/api/') && (servesPluginApi || !path.startsWith('/api/v1/'))) {
    return `${runtime.baseUrl.replace(/\/$/, '')}${path}`;
  }

  const routePath = path.startsWith('/api/v1/')
    ? path.slice('/api/v1/'.length)
    : path.replace(/^\//, '');
  const queryAt = routePath.indexOf('?');
  const route = queryAt >= 0 ? routePath.slice(0, queryAt) : routePath;
  const query = queryAt >= 0 ? routePath.slice(queryAt + 1) : '';

  if (runtime.apiUrl.includes('{path}')) {
    const expanded = runtime.apiUrl.replace('{path}', encodeURIComponent(route));
    const url = new URL(expanded, window.location.origin);
    new URLSearchParams(query).forEach((value, key) => url.searchParams.append(key, value));
    return url.toString();
  }

  const url = new URL(runtime.apiUrl, window.location.origin);
  if (url.searchParams.has('rest_route')) {
    const baseRoute = String(url.searchParams.get('rest_route') || '').replace(/\/$/, '');
    url.searchParams.set('rest_route', `${baseRoute}/${route.replace(/^\//, '')}`);
  } else {
    url.pathname = `${url.pathname.replace(/\/$/, '')}/${route.replace(/^\//, '')}`;
  }
  new URLSearchParams(query).forEach((value, key) => url.searchParams.append(key, value));
  return url.toString();
}

/** Host config when a native (non-WordPress) adapter serves the API. */
function nativeHost(): (Partial<VeyopHostRuntimeConfig> & { apiUrl: string }) | null {
  if (typeof window === 'undefined') return null;
  const host = window.VeyopHostConfig;
  if (!host?.apiUrl || !host.platform || host.platform === 'wordpress') return null;
  return host as Partial<VeyopHostRuntimeConfig> & { apiUrl: string };
}

function queryString(query: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  return params.toString();
}

/**
 * Designer AJAX endpoint (`?type=`). WordPress keeps `{veyopUrl}ajax.php`;
 * OpenCart/PrestaShop route `ajax/{type}` through their API controller.
 */
export function veyopAjaxUrl(
  type: string,
  query: Record<string, string | number | undefined> = {},
  wordpressBase = '',
): string {
  const qs = queryString(query);
  const host = nativeHost();
  if (host) {
    return buildRuntimeRequestUrl(
      { baseUrl: host.baseUrl ?? '', apiUrl: host.apiUrl, platform: host.platform },
      `ajax/${encodeURIComponent(type)}${qs ? `?${qs}` : ''}`,
    );
  }
  const base = (wordpressBase || window.VeyopHostConfig?.baseUrl || '').replace(/\/?$/, '/');
  return `${base}ajax.php?type=${encodeURIComponent(type)}${qs ? `&${qs}` : ''}`;
}

/**
 * Storefront action endpoint: WordPress `admin-ajax.php?action=`; native
 * hosts route `action/{name}` through their API controller.
 */
export function veyopActionUrl(action: string, wordpressSiteUrl = ''): string {
  const host = nativeHost();
  if (host) {
    return buildRuntimeRequestUrl(
      { baseUrl: host.baseUrl ?? '', apiUrl: host.apiUrl, platform: host.platform },
      `action/${encodeURIComponent(action)}`,
    );
  }
  const site = (wordpressSiteUrl || '/').replace(/\/?$/, '/');
  return `${site}wp-admin/admin-ajax.php?action=${encodeURIComponent(action)}`;
}

declare global {
  interface Window {
    Veyop?: VeyopRuntime;
    VeyopHostConfig?: Partial<VeyopHostRuntimeConfig>;
  }
}

const VEYOP_RUNTIME_VERSION = '1.3.0';
const readyPayloads = new Map<VeyopRuntimeSurface, unknown>();
const readyHandlers = new Map<VeyopRuntimeSurface, Set<(payload: unknown) => void>>();

function resolveRuntimeAssetUrl(
  raw: string,
  siteUrl: string,
  veyopUrl: string,
  options: { proxy?: boolean } = {},
): string {
  const src = String(raw ?? '').trim();
  if (!src || src.startsWith('data:')) return src;
  let resolved = src;
  if (src.startsWith('//')) resolved = `${window.location.protocol || 'https:'}${src}`;
  else if (src.startsWith('/')) {
    try {
      resolved = new URL(src, siteUrl || window.location.origin).href;
    } catch {
      resolved = src;
    }
  } else if (!/^https?:\/\//i.test(src)) {
    resolved = `${veyopUrl.replace(/\/$/, '')}/${src.replace(/^\//, '')}`;
  }
  if (options.proxy && /^https?:\/\//i.test(resolved)) {
    try {
      if (new URL(resolved).origin !== window.location.origin) {
        return `${veyopUrl.replace(/\/$/, '')}/public/image-proxy.php?src=${encodeURIComponent(resolved)}`;
      }
    } catch {
      return resolved;
    }
  }
  return resolved;
}

function whenRuntimeReady(
  surface: VeyopRuntimeSurface,
  handler: (payload: unknown) => void,
): () => void {
  if (readyPayloads.has(surface)) {
    queueMicrotask(() => handler(readyPayloads.get(surface)));
    return () => {};
  }
  const handlers = readyHandlers.get(surface) ?? new Set();
  handlers.add(handler);
  readyHandlers.set(surface, handlers);
  return () => handlers.delete(handler);
}

export function markVeyopSurfaceReady(surface: VeyopRuntimeSurface, payload?: unknown): void {
  readyPayloads.set(surface, payload);
  for (const handler of readyHandlers.get(surface) ?? []) {
    try {
      handler(payload);
    } catch (error) {
      console.warn(`[veyop] ${surface} ready handler failed`, error);
    }
  }
  readyHandlers.delete(surface);
}

export function installVeyopRuntime(): void {
  if (typeof window === 'undefined') {
    return;
  }
  type Ev = string & keyof DesignEventMap;
  const admin = typeof document !== 'undefined' ? document.querySelector('veyop-admin') : null;
  const baseUrl = String(window.VeyopHostConfig?.baseUrl || admin?.getAttribute('base-url') || '').replace(/\/$/, '');
  const csrfToken = admin?.getAttribute('csrf-token') || '';
  const runtime: VeyopHostRuntimeConfig = {
    platform: window.VeyopHostConfig?.platform ?? 'wordpress',
    baseUrl,
    apiUrl: String(window.VeyopHostConfig?.apiUrl || `${baseUrl}/api/v1`).replace(/\/$/, ''),
    assetsUrl: String(window.VeyopHostConfig?.assetsUrl || baseUrl).replace(/\/$/, ''),
    requestHeaders: {
      ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
      ...(window.VeyopHostConfig?.requestHeaders ?? {}),
    },
  };
  const { http, setCsrfToken } = createRuntimeHttp(runtime);
  trackCsrfStaleness(runtime, http, setCsrfToken);
  window.Veyop = {
    version: VEYOP_RUNTIME_VERSION,
    apiVersion: 2,
    http,
    api: http,
    runtime,
    hooks: {
      on: (event, handler) =>
        eventBus.on(event as Ev, handler as (payload: unknown) => void),
      off: (event, handler) =>
        eventBus.off(event as Ev, handler as (payload: unknown) => void),
      emit: (event, payload) => eventBus.emit(event as Ev, payload as never),
      addFilter,
      removeFilter,
      applyFilters,
      addAction,
      removeAction,
      doAction,
      registerSlot,
      removeSlot,
      getSlotEntries,
      whenReady: whenRuntimeReady,
    },
    tools: {
      registerTextTool,
      getTextTools,
      registerImageTool,
      getImageTools,
    },
    itemRenderers: {
      register: registerItemRenderer,
      find: findItemRenderer,
    },
    panels: {
      register: registerEditPanel,
      getEditPanels,
      find: findEditPanel,
    },
    perf: { report: bootReport },
    itemBounds: {
      register: registerItemBoundsResolver,
      getResolvers: getItemBoundsResolvers,
      resolve: resolveItemBounds,
      fitToBox: fitItemToBox,
      measureContent: measureItemContent,
    },
    textStyles: {
      register: registerTextStyleSupport,
      resolve: resolveTextStyleSupport,
    },
    admin: {
      registerPage: registerAdminPage,
      getPages: getAdminPages,
      findPage: findAdminPage,
      registerPageTab: registerAdminPageTab,
      getPageTabs: getAdminPageTabs,
      registerWidget: registerAdminWidget,
      getWidgets: getAdminWidgets,
      registerWooProductSection,
      getWooProductSections,
      registerPrintingMethodProvider,
      getPrintingMethodProviders,
      resolvePrintingMethodProviderId,
      registerCatalogEditPanel,
      registerCatalogListBadges,
      getCatalogEditPanels,
      registerCatalogCanvasOverlay,
      getCatalogCanvasOverlays,
    },
    designer: {
      registerOverlay: registerDesignerOverlay,
      getOverlays: getDesignerOverlays,
      registerStatusPill,
      getStatusPills,
      registerLeftPanel: registerDesignerLeftPanel,
      getLeftPanels: getDesignerLeftPanels,
      findLeftPanel: findDesignerLeftPanel,
      registerLayout: registerDesignerLayout,
      getLayouts: getDesignerLayouts,
      findLayout: findDesignerLayout,
      components: undefined,
      pinia: undefined,
      updateItem: undefined,
      beginHistoryBatch: undefined,
      endHistoryBatch: undefined,
      setPricingContext: undefined,
      commands: undefined,
      getContext: undefined,
      resolveAssetUrl: undefined,
    },
    views: {
      register: registerProductView,
      getAll: getProductViews,
      resolve: resolveProductView,
      isViewKey,
      coreViews: CORE_VIEW_NAMES,
    },
    uploads: {
      registerProcessor: registerUploadProcessor,
      getProcessors: getUploadProcessors,
      uploadImage,
    },
    imagePipeline: {
      registerEffectPreset,
      getEffectPresets,
      findEffectPreset,
      registerImageRasterizer,
      getImageRasterizers,
      registerImageExporter,
      getImageExporters,
      registerPrintMethodPack,
      getPrintMethodPacks,
      registerItemSvgExporter,
      getItemSvgExporters,
      registerPrintAreaShape,
      getPrintAreaShapes,
    },
    preflight: {
      registerRule: registerPreflightRule,
      getRules: getPreflightRules,
      buildReport: undefined,
      normalizeProfile: undefined,
    },
    ui: {
      registerMobileNavItem,
      getMobileNavItems,
      registerToolbarAction,
      getToolbarActions,
      notify: (type, message) => {
        if (type === 'success') notifySuccess(message);
        else if (type === 'error') notifyError(message);
        else if (type === 'warning') notifyWarning(message);
        else notifyInfo(message);
      },
      confirm: async (message) => window.confirm(message),
    },
  };
}

/**
 * Bind Pinia-backed host commands after the designer app creates its store.
 */
let designerHostBindingVersion = 0;

export function bindDesignerHostCommands(pinia: Pinia): Promise<void> {
  if (typeof window === 'undefined' || !window.Veyop?.designer) return Promise.resolve();
  const bindingVersion = ++designerHostBindingVersion;
  const runtime = window.Veyop;

  // Lazy imports avoid circular deps at module load time.
  return Promise.all([
    import('../designer-app/stores/designStore'),
    import('../designer-app/stores/toolsStore'),
    import('../designer-app/stores/configStore'),
    import('../designer-app/stores/productStore'),
    import('../designer-app/composables/useFontEmbedding'),
  ]).then(([
    { useDesignStore },
    { useToolsStore },
    { useConfigStore },
    { useProductStore },
    { useFontEmbedding },
  ]) => {
      if (bindingVersion !== designerHostBindingVersion || !runtime.designer) return;
      const { getEmbeddableFontFaceCss } = useFontEmbedding(pinia);
      runtime.imagePipeline.getEmbeddableFontFaceCss = (family, weight = 'normal', style = 'normal') =>
        getEmbeddableFontFaceCss(family, weight, style).catch(() => '');
      const design = useDesignStore(pinia);
      const tools = useToolsStore(pinia);
      const config = useConfigStore(pinia);
      const product = useProductStore(pinia);
      runtime.designer.pinia = pinia;
      runtime.designer.updateItem = (id, patch) => design.updateItem(id, patch);
      runtime.designer.beginHistoryBatch = () => tools.beginHistoryBatch();
      runtime.designer.endHistoryBatch = (reason?) => tools.endHistoryBatch(reason);
      runtime.designer.setPricingContext = (key, value) => {
        const next = { ...(config.data.pricingContext ?? {}) };
        if (value === null || value === undefined) delete next[key];
        else next[key] = value;
        config.data.pricingContext = next;
      };
      runtime.designer.commands = {
        addItem: (item, options = {}) => {
          if (options.placement !== 'print-area-center') {
            design.addItem(item);
            return;
          }
          const stageWidth = Math.max(1, Number(config.data.items.boxWidth) || 500);
          const stageHeight = Math.max(1, Number(config.data.items.boxHeight) || 500);
          const area = resolveViewAreaSizes(
            config.data.items.area,
            stageWidth,
            stageHeight,
          )[design.activeView] ?? { width: stageWidth, height: stageHeight };
          design.addItem(centerItemInPrintArea(item, area));
        },
        removeItem: (id) => design.removeItem(id),
        updateItem: (id, patch) => design.updateItem(id, patch),
        selectItem: (id) => design.selectItem(id),
        getItems: () => design.items as Record<string, DesignItem[]>,
        setProductColor: (index) => product.changeColor(index),
        loadDesign: (state) => {
          const views = Array.isArray(state.views) ? state.views.map(String).filter(isViewKey) : [];
          if (views.length) design.setAvailableViews(views);
          const stageWidth = Math.max(1, Number(config.data.items.boxWidth) || 500);
          const stageHeight = Math.max(1, Number(config.data.items.boxHeight) || 500);
          const targetAreas = resolveViewAreaSizes(
            config.data.items.area,
            stageWidth,
            stageHeight,
          );
          const rawItems = (state.items ?? {}) as Partial<Record<string, DesignItem[]>>;
          // Same contain-fill the designer bootstrap and the product mockup use, so a template
          // picked from the panel lands at the same size as the one assigned to the product.
          const scaledItems = fitDesignItemsToPrintAreas(
            rawItems,
            targetAreas,
            {
              margins: printSafetyMargins(targetAreas, resolveViewPhysicalSizesCm(product.product?.params), config.data.items.preflightProfile),
              sourceAreas: readAuthoringAreaSizes(state.metadata),
              measure: measureItemContent,
              fitToBox: fitItemToBox,
              growToContent: true,
            },
          );
          design.loadDesignState({
            items: scaledItems as Parameters<typeof design.loadDesignState>[0]['items'],
            activeView: typeof state.activeView === 'string' ? state.activeView : undefined,
            designId: null,
            designKey: '',
            designerId: '',
            designFile: '',
          });
        },
      };
      runtime.designer.getContext = () => ({
        productId: config.data.productId,
        parentId: config.data.parentId,
        baseUrl: config.data.veyopUrl,
        printingCodes: (config.data.printingTypes ?? []).map((row) => String(row.printing_code || '')).filter(Boolean),
        activePrintCode: String(product.selectedPrintingCode || ''),
        activePrintMethodPack: resolvePrintingMethodProviderId(product.selectedPrinting),
        activeView: design.activeView,
        embroideryProfile: config.data.items.embroideryProfile,
      });
      runtime.designer.resolveAssetUrl = (raw, options) => resolveRuntimeAssetUrl(
        raw,
        config.data.siteUrl,
        config.data.veyopUrl,
        options,
      );
  });
}

export type {
  VeyopTextTool,
  VeyopImageTool,
  VeyopItemRenderer,
  VeyopEditPanel,
  VeyopItemBounds,
  VeyopItemBoundsResolver,
  VeyopTextStyleSupport,
  VeyopTextStyleSupportProvider,
  VeyopAdminPage,
  VeyopAdminPageTab,
  VeyopAdminNavGroup,
  VeyopWooProductSection,
  VeyopPrintingMethodProvider,
  VeyopCatalogEditPanel,
  VeyopCatalogListBadge,
  VeyopCatalogListBadgeProvider,
  VeyopCatalogCanvasOverlay,
  VeyopDesignerOverlay,
  VeyopDesignerLeftPanel,
  AdminMaterialIcon,
  AdminPageIcon,
  DesignerIconInput,
  DesignerIconSpec,
  DesignerIconType,
  VeyopDesignerLayout,
  VeyopUploadNoticeType,
  VeyopUploadProcessor,
  VeyopUploadedImage,
  VeyopUploadProcessorResult,
  VeyopUploadProcessorContext,
  VeyopUploadProcessorError,
  VeyopProductView,
} from './extensionRegistry';

export type {
  VeyoHookSlotEntry,
  VeyoHookSlotRender,
} from './VeyoHooks';

export type { ImageTransform } from './types';
